import { useEffect, useRef } from "react";

import { runInAction } from "mobx";
import { observer } from "mobx-react-lite";
import { useTranslations } from "next-intl";

import { twMerge } from "@/utils/twMerge";

import type { SuggestionsModel } from "@/models/SuggestionsModel";

type Props = {
  suggestions: SuggestionsModel;
};

export const SuggestionsSearchInput = observer(function SuggestionsSearchInput({
  suggestions,
}: Props) {
  const t = useTranslations("suggestions.search");
  const inputRef = useRef<HTMLInputElement>(null);
  const { query } = suggestions;

  useEffect(() => {
    const input = inputRef.current;

    if (query) {
      input?.focus();
      return undefined;
    }

    if (document.activeElement === input) {
      suggestions.editor.doc.focus();
    }

    return undefined;
  }, [query, suggestions]);

  useEffect(() => {
    const input = inputRef.current;

    return () => {
      const { activeElement } = document;

      if (activeElement === input || activeElement === document.body || !activeElement) {
        suggestions.editor.doc.focus();
      }
    };
  }, [suggestions]);

  return (
    <div
      className={twMerge(
        "flex items-center px-2",
        query ? "border-b border-gray-200 dark:border-gray-600" : "h-0 overflow-hidden",
      )}
    >
      <input
        ref={inputRef}
        aria-label={t("label")}
        placeholder={t("placeholder")}
        value={query}
        onChange={(event) =>
          runInAction(() => {
            suggestions.query = event.target.value;
          })
        }
        className="flex-1 min-w-0 h-9 px-1.5 bg-transparent border-0 outline-none text-ellipsis placeholder:text-secondary"
        data-testid="suggestions-search"
      />
    </div>
  );
});
